/**
 * Module dependencies.
 */

import React, { useCallback, useEffect } from 'react';
import styles from '../sales-list.module.less';
import { CrudTemplate, WrapperWidget } from 'app/components/organisms/crud/form-types';
import { Language } from 'app/types/language';
import { FieldValues, UseFormReturn } from 'react-hook-form';
import { Button } from 'app/components/atoms/button/button';
import { useTranslation } from 'react-i18next';
import { range } from 'lodash';
import { CrudFormRender } from 'app/components/organisms/crud/crud-form';
import classNames from 'classnames';
import { posologiesFormField } from './form-posology-config';

/**
 * `Props` type.
 */

type Props<T> = {
  children: JSX.Element;
  template: CrudTemplate<T>;
  languages: Language[];
  currentLanguage: string | undefined;
  mainLanguage: string | undefined;
  metadata: any;
  formType: 'add' | 'edit';
  name: string;
  item: WrapperWidget;
  form: UseFormReturn<FieldValues, any>;
};

/**
 * Export `PosologiesForm` component.
 */

export function PosologiesForm<T>({ item, ...rest }: Props<T>): JSX.Element {
  const [translate] = useTranslation();

  const formFields = useCallback(
    (index: number) => {
      return posologiesFormField(translate, `${item.metadata.prefix}.posology[${index}]`);
    },
    [item.metadata.prefix, translate]
  );

  const lines = rest.form.getValues(`${item.metadata.prefix}.posology`);

  useEffect(() => {
    if (lines === undefined) {
      rest.form.setValue(`${item.metadata.prefix}.posology`, []);

      return;
    }
  }, [item.metadata.prefix, lines, rest.form]);

  return (
    <div className={styles.posologiesForm}>
      {range(lines?.length ?? 0).map(index => (
        <div className={classNames(styles.addWrapper, styles.level2)} key={index}>
          <div className={styles.wrapperHeader}>
            {'Posology: '}
            {index + 1}
          </div>

          <CrudFormRender {...rest} formFields={formFields(index)} />
        </div>
      ))}

      <div className={classNames(styles.orderItemHeader, styles.actionsPadding)}>
        <div>
          <Button
            onClick={() => {
              rest.form.setValue(`${item.metadata.prefix}.posology`, [...lines, {}]);
            }}
            type={'primary'}
          >
            {translate('users.sales.add.actions.addPosology')}
          </Button>
        </div>

        {lines?.length > 0 && (
          <div>
            <Button
              danger
              disabled={lines === 0}
              onClick={() => {
                const newLines = lines.slice(0, lines.length - 1);

                rest.form.setValue(`${item.metadata.prefix}.posology`, newLines);
              }}
            >
              {translate('users.sales.add.actions.removeLine')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
